import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaChevronDown, FaChevronUp, FaMapMarkerAlt } from 'react-icons/fa';
import './Product.css';

const Product = ({ translations }) => {
  const { slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [coverImageAvailable, setCoverImageAvailable] = useState(true);
  const totalImages = 20;

  const objectTranslations = translations?.components?.products?.translations || {};
  const productKeyBase = `products.${slug}`;

  const productTitleText = objectTranslations[`${productKeyBase}.title`];
  const productBodyText = objectTranslations[`${productKeyBase}.body`];
  const productAddressText = objectTranslations[`${productKeyBase}.address`];
  const productFeature1Text = objectTranslations[`${productKeyBase}.feature.1`];
  const productFeature2Text = objectTranslations[`${productKeyBase}.feature.2`];
  const productFeature3Text = objectTranslations[`${productKeyBase}.feature.3`];
  const productFeature4Text = objectTranslations[`${productKeyBase}.feature.4`];
  const productFeatures = [
    productFeature1Text,
    productFeature2Text,
    productFeature3Text,
    productFeature4Text,
  ].filter((item) => item && String(item).trim());
  const productAddressMapsUrl = productAddressText
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(productAddressText)}`
    : '';

  const getImage = (n) => {
    return `/img/products/${slug}/foto${n}.png`;
  };

  const photoInitialParam = searchParams.get('photoInitial');
  const coverPhotoMatch = photoInitialParam ? photoInitialParam.match(/^foto(\d+)$/i) : null;
  const coverPhotoIndex = coverPhotoMatch ? Number(coverPhotoMatch[1]) : null;
  const coverImageSrc =
    coverPhotoIndex && coverPhotoIndex >= 1 && coverPhotoIndex <= totalImages
      ? getImage(coverPhotoIndex)
      : null;
  const shouldRenderCover = Boolean(coverImageSrc && coverImageAvailable && productTitleText);

  useEffect(() => {
    setCoverImageAvailable(true);
  }, [slug, photoInitialParam]);

  const galleryItems = [];
  for (let i = 1; i <= totalImages; i++) {
    galleryItems.push({ index: i, src: getImage(i) });
  }

  const goPrev = () => {
    setLightboxIndex((prev) => {
      if (prev === null) return prev;
      return prev === 1 ? totalImages : prev - 1;
    });
  };

  const goNext = () => {
    setLightboxIndex((prev) => {
      if (prev === null) return prev;
      return prev === totalImages ? 1 : prev + 1;
    });
  };

  return (
    <div className="product-page">
      <header className="product-header">
        <div className="product-header-inner">
          <Link to="/" className="product-back">
            <div className="sticky-logo">
              <h1>
                <img src="/img/realjobs.svg" alt="" className="logo-img" />
                <span className="brand-name">Real </span> Jobs
              </h1>
            </div>
          </Link>
        </div>
      </header>

      <section className="product-content area-padding">
        <div className="container product-content-centered">
          {shouldRenderCover ? (
            <div className="product-cover">
              <img
                src={coverImageSrc}
                alt={productTitleText}
                className="product-cover-image"
                onError={() => setCoverImageAvailable(false)}
              />
              <div className="product-cover-overlay">
                <h2>{productTitleText}</h2>
              </div>
            </div>
          ) : (
            productTitleText && (
              <div className="section-headline text-center">
                <h2>{productTitleText}</h2>
              </div>
            )
          )}
          {productFeatures.length > 0 && (
            <div className="product-feature-balloons">
              {productFeatures.map((feature, index) => (
                <span key={index} className="product-feature-balloon">{feature}</span>
              ))}
            </div>
          )}
          {productBodyText && (
            <div className="product-details-wrap">
              <button
                type="button"
                className="product-details-toggle"
                onClick={() => setDetailsOpen((prev) => !prev)}
                aria-expanded={detailsOpen}
              >
                {detailsOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              <div className={`product-body ${detailsOpen ? 'is-open' : 'is-closed'}`}>{productBodyText}</div>
            </div>
          )}
          {productAddressText && (
            <p className="product-address">
              <a
                className="product-address-link"
                href={productAddressMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaMapMarkerAlt className="product-address-icon" />
                <span className="product-address-text">{productAddressText}</span>
              </a>
            </p>
          )}
        </div>
      </section>

      <section className="product-gallery-section area-padding">
        <div className="container">
          <div className="product-gallery">
            {galleryItems.map(({ index, src }) => (
              <button key={index} type="button" className="product-gallery-item" onClick={() => setLightboxIndex(index)}>
                <img src={src} alt="" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {lightboxIndex !== null && (
        <div className="product-lightbox" onClick={() => setLightboxIndex(null)} role="button" tabIndex={0}>
          <button
            type="button"
            className="product-lightbox-close"
            onClick={() => setLightboxIndex(null)}
          >
            X
          </button>
          <img src={getImage(lightboxIndex)} alt="" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            className="product-lightbox-nav product-lightbox-nav-prev"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
          >
            <FaChevronLeft className="product-lightbox-nav-icon" />
          </button>
          <button
            type="button"
            className="product-lightbox-nav product-lightbox-nav-next"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
          >
            <FaChevronRight className="product-lightbox-nav-icon" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Product;

